import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ChevronLeft, ChevronRight, Upload, X } from 'lucide-react';
import theme from '../theme';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BasicPortfolioWizard = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    // Step 1
    name: '',
    description: '',
    category: '',
    // Step 2
    coverImage: null,
    coverImagePreview: '',
    // Step 3
    kickbackPercent: 10,
    numNFTs: 100,
    termsText: '',
    // Step 4
    curatorId: '',
    managementType: 'curator',
    // Step 5
    confirmed: false
  });

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        updateField('coverImage', file);
        updateField('coverImagePreview', reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return formData.name && formData.description && formData.category;
      case 2:
        return formData.coverImagePreview;
      case 3:
        return formData.kickbackPercent && formData.numNFTs;
      case 4:
        return formData.managementType;
      case 5:
        return formData.confirmed;
      default:
        return false;
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const portfolioData = {
        name: formData.name,
        description: formData.description,
        category: formData.category,
        kickback_percent: formData.kickbackPercent,
        nft_image_url: formData.coverImagePreview,
        management_type: formData.managementType,
        curator_id: formData.curatorId || null,
        num_nfts: formData.numNFTs,
        terms_text: formData.termsText,
        status: 'DRAFT',
        activation_threshold_value: 25000,
        full_activation_level: 50000
      };

      const res = await axios.post(`${API}/portfolios/create-full`, portfolioData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.data.success) {
        alert('Portfolio created successfully!');
        navigate('/creator/portfolios');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Failed to create portfolio');
    } finally {
      setLoading(false);
    }
  };

  const mockCurators = [
    { id: 'curator1', name: 'Elite Curator Group', specialties: 'Luxury, Streetwear' },
    { id: 'curator2', name: 'Urban Partners', specialties: 'Digital, Apparel' },
    { id: 'curator3', name: 'Sustainable Ventures', specialties: 'Eco-Friendly' }
  ];

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #F8F9FB, #FFFFFF)',
      padding: '40px 20px'
    }}>
      <div style={{ maxWidth: '900px', margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
          <div>
            <h1 style={{ fontSize: '32px', fontWeight: 700, color: theme.colors.text.primary, marginBottom: '8px' }}>
              Create Portfolio
            </h1>
            <p style={{ fontSize: '16px', color: theme.colors.text.secondary }}>Step {currentStep} of 5</p>
          </div>
          <button
            onClick={() => navigate('/creator/dashboard')}
            style={{
              padding: '8px 16px',
              background: 'transparent',
              border: `1px solid ${theme.colors.borders}`,
              borderRadius: theme.radius.sm,
              color: theme.colors.text.secondary,
              fontSize: '14px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            <X size={16} />
            Cancel
          </button>
        </div>

        <div style={{
          height: '4px',
          background: theme.colors.bg.tertiary,
          borderRadius: '2px',
          marginBottom: '40px'
        }}>
          <div style={{
            height: '100%',
            background: theme.colors.primary,
            width: `${(currentStep / 5) * 100}%`,
            transition: 'width 0.3s'
          }} />
        </div>

        <div style={{
          background: theme.colors.bg.card,
          borderRadius: theme.radius.card,
          padding: '40px',
          boxShadow: theme.colors.shadows.lg,
          border: `1px solid ${theme.colors.borders}`
        }}>
          {/* STEP 1: Basic Details */}
          {currentStep === 1 && (
            <div>
              <h2 style={{ fontSize: '24px', fontWeight: 700, color: theme.colors.text.primary, marginBottom: '24px' }}>
                Basic Details
              </h2>
              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: theme.colors.text.primary, marginBottom: '8px' }}>
                  Portfolio Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => updateField('name', e.target.value)}
                  placeholder="e.g., Summer 2025 Collection"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: `1px solid ${theme.colors.borders}`,
                    borderRadius: theme.radius.sm,
                    fontSize: '14px',
                    color: theme.colors.text.primary,
                    background: '#FFFFFF'
                  }}
                />
              </div>
              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: theme.colors.text.primary, marginBottom: '8px' }}>
                  Description *
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => updateField('description', e.target.value)}
                  placeholder="Describe your portfolio..."
                  rows={4}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: `1px solid ${theme.colors.borders}`,
                    borderRadius: theme.radius.sm,
                    fontSize: '14px',
                    color: theme.colors.text.primary,
                    background: '#FFFFFF',
                    resize: 'vertical'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: theme.colors.text.primary, marginBottom: '8px' }}>
                  Category *
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => updateField('category', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: `1px solid ${theme.colors.borders}`,
                    borderRadius: theme.radius.sm,
                    fontSize: '14px',
                    color: theme.colors.text.primary,
                    background: '#FFFFFF'
                  }}
                >
                  <option value="">Select category</option>
                  <option value="apparel">Apparel</option>
                  <option value="art">Art</option>
                  <option value="music">Music</option>
                  <option value="gaming">Gaming</option>
                  <option value="events">Events</option>
                </select>
              </div>
            </div>
          )}

          {/* STEP 2: Cover Image */}
          {currentStep === 2 && (
            <div>
              <h2 style={{ fontSize: '24px', fontWeight: 700, color: theme.colors.text.primary, marginBottom: '24px' }}>
                Portfolio Cover Image
              </h2>
              {!formData.coverImagePreview ? (
                <label style={{
                  display: 'block',
                  padding: '60px 20px',
                  border: `2px dashed ${theme.colors.borders}`,
                  borderRadius: theme.radius.md,
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}>
                  <Upload size={40} style={{ color: theme.colors.primary, margin: '0 auto 16px' }} />
                  <div style={{ fontSize: '16px', fontWeight: 600, color: theme.colors.text.primary, marginBottom: '8px' }}>
                    Upload Cover Image
                  </div>
                  <div style={{ fontSize: '14px', color: theme.colors.text.muted }}>
                    PNG, JPG, GIF (Recommended: 1200x600px)
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    style={{ display: 'none' }}
                  />
                </label>
              ) : (
                <div style={{ textAlign: 'center' }}>
                  <img
                    src={formData.coverImagePreview}
                    alt="Cover"
                    style={{
                      maxWidth: '100%',
                      maxHeight: '400px',
                      borderRadius: theme.radius.md,
                      marginBottom: '16px'
                    }}
                  />
                  <div>
                    <button
                      onClick={() => {
                        updateField('coverImagePreview', '');
                        updateField('coverImage', null);
                      }}
                      style={{
                        padding: '8px 16px',
                        background: theme.colors.bg.secondary,
                        color: theme.colors.text.primary,
                        border: `1px solid ${theme.colors.borders}`,
                        borderRadius: theme.radius.sm,
                        fontSize: '14px',
                        cursor: 'pointer'
                      }}
                    >
                      Change Image
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* STEP 3: Smart Agreement */}
          {currentStep === 3 && (
            <div>
              <h2 style={{ fontSize: '24px', fontWeight: 700, color: theme.colors.text.primary, marginBottom: '24px' }}>
                Smart Agreement Terms
              </h2>
              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: theme.colors.text.primary, marginBottom: '8px' }}>
                  Activation Contribution % *
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={formData.kickbackPercent}
                  onChange={(e) => updateField('kickbackPercent', parseFloat(e.target.value))}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: `1px solid ${theme.colors.borders}`,
                    borderRadius: theme.radius.sm,
                    fontSize: '14px',
                    color: theme.colors.text.primary,
                    background: '#FFFFFF'
                  }}
                />
                <p style={{ fontSize: '12px', color: theme.colors.text.muted, marginTop: '4px' }}>
                  Percentage of each sale that goes to Activation Pool
                </p>
              </div>
              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: theme.colors.text.primary, marginBottom: '8px' }}>
                  Number of NFTs *
                </label>
                <input
                  type="number"
                  min="1"
                  value={formData.numNFTs}
                  onChange={(e) => updateField('numNFTs', parseInt(e.target.value))}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: `1px solid ${theme.colors.borders}`,
                    borderRadius: theme.radius.sm,
                    fontSize: '14px',
                    color: theme.colors.text.primary,
                    background: '#FFFFFF'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: theme.colors.text.primary, marginBottom: '8px' }}>
                  Additional Terms (optional)
                </label>
                <textarea
                  value={formData.termsText}
                  onChange={(e) => updateField('termsText', e.target.value)}
                  placeholder="Any additional terms or conditions..."
                  rows={4}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: `1px solid ${theme.colors.borders}`,
                    borderRadius: theme.radius.sm,
                    fontSize: '14px',
                    color: theme.colors.text.primary,
                    background: '#FFFFFF',
                    resize: 'vertical'
                  }}
                />
              </div>
            </div>
          )}

          {/* STEP 4: Curator Selection */}
          {currentStep === 4 && (
            <div>
              <h2 style={{ fontSize: '24px', fontWeight: 700, color: theme.colors.text.primary, marginBottom: '24px' }}>
                Curator Selection
              </h2>
              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '16px',
                  border: `2px solid ${formData.managementType === 'curator' ? theme.colors.primary : theme.colors.borders}`,
                  borderRadius: theme.radius.sm,
                  background: formData.managementType === 'curator' ? `${theme.colors.primary}10` : '#FFFFFF',
                  cursor: 'pointer',
                  marginBottom: '12px'
                }}>
                  <input
                    type="radio"
                    name="managementType"
                    value="curator"
                    checked={formData.managementType === 'curator'}
                    onChange={(e) => updateField('managementType', e.target.value)}
                  />
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: 600, color: theme.colors.text.primary }}>Work with a Curator</div>
                    <div style={{ fontSize: '12px', color: theme.colors.text.secondary }}>Professional management</div>
                  </div>
                </label>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '16px',
                  border: `2px solid ${formData.managementType === 'diy' ? theme.colors.primary : theme.colors.borders}`,
                  borderRadius: theme.radius.sm,
                  background: formData.managementType === 'diy' ? `${theme.colors.primary}10` : '#FFFFFF',
                  cursor: 'pointer'
                }}>
                  <input
                    type="radio"
                    name="managementType"
                    value="diy"
                    checked={formData.managementType === 'diy'}
                    onChange={(e) => updateField('managementType', e.target.value)}
                  />
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: 600, color: theme.colors.text.primary }}>Self-Managed (DIY)</div>
                    <div style={{ fontSize: '12px', color: theme.colors.text.secondary }}>Manage yourself</div>
                  </div>
                </label>
              </div>
              {formData.managementType === 'curator' && (
                <div>
                  <h3 style={{ fontSize: '16px', fontWeight: 600, color: theme.colors.text.primary, marginBottom: '12px' }}>
                    Select a Curator
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {mockCurators.map(curator => (
                      <label
                        key={curator.id}
                        style={{
                          padding: '16px',
                          border: `2px solid ${formData.curatorId === curator.id ? theme.colors.primary : theme.colors.borders}`,
                          borderRadius: theme.radius.sm,
                          background: formData.curatorId === curator.id ? `${theme.colors.primary}10` : theme.colors.bg.secondary,
                          cursor: 'pointer'
                        }}
                      >
                        <input
                          type="radio"
                          name="curator"
                          checked={formData.curatorId === curator.id}
                          onChange={() => updateField('curatorId', curator.id)}
                          style={{ marginRight: '12px' }}
                        />
                        <div style={{ display: 'inline-block' }}>
                          <div style={{ fontSize: '14px', fontWeight: 600, color: theme.colors.text.primary }}>{curator.name}</div>
                          <div style={{ fontSize: '12px', color: theme.colors.text.secondary }}>{curator.specialties}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* STEP 5: Review */}
          {currentStep === 5 && (
            <div>
              <h2 style={{ fontSize: '24px', fontWeight: 700, color: theme.colors.text.primary, marginBottom: '24px' }}>
                Review & Submit
              </h2>
              <div style={{
                background: theme.colors.bg.secondary,
                padding: '24px',
                borderRadius: theme.radius.md,
                marginBottom: '24px'
              }}>
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ fontSize: '12px', fontWeight: 600, color: theme.colors.text.muted, marginBottom: '4px' }}>PORTFOLIO NAME</div>
                  <div style={{ fontSize: '16px', color: theme.colors.text.primary }}>{formData.name}</div>
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ fontSize: '12px', fontWeight: 600, color: theme.colors.text.muted, marginBottom: '4px' }}>CATEGORY</div>
                  <div style={{ fontSize: '14px', color: theme.colors.text.primary }}>{formData.category}</div>
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ fontSize: '12px', fontWeight: 600, color: theme.colors.text.muted, marginBottom: '4px' }}>COVER IMAGE</div>
                  {formData.coverImagePreview && (
                    <img src={formData.coverImagePreview} alt="Cover" style={{ maxWidth: '200px', borderRadius: theme.radius.sm }} />
                  )}
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ fontSize: '12px', fontWeight: 600, color: theme.colors.text.muted, marginBottom: '4px' }}>CONTRIBUTION</div>
                  <div style={{ fontSize: '14px', color: theme.colors.text.primary }}>{formData.kickbackPercent}% | {formData.numNFTs} NFTs</div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', fontWeight: 600, color: theme.colors.text.muted, marginBottom: '4px' }}>MANAGEMENT</div>
                  <div style={{ fontSize: '14px', color: theme.colors.text.primary }}>
                    {formData.managementType === 'curator' ? `Curator: ${mockCurators.find(c => c.id === formData.curatorId)?.name || 'Not selected'}` : 'Self-Managed'}
                  </div>
                </div>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={formData.confirmed}
                  onChange={(e) => updateField('confirmed', e.target.checked)}
                />
                <span style={{ fontSize: '14px', color: theme.colors.text.primary }}>
                  I confirm this information is accurate and ready for submission
                </span>
              </label>
            </div>
          )}

          {/* Navigation */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: '40px',
            paddingTop: '24px',
            borderTop: `1px solid ${theme.colors.borders}`
          }}>
            <button
              onClick={() => setCurrentStep(prev => Math.max(1, prev - 1))}
              disabled={currentStep === 1}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 24px',
                border: `1px solid ${theme.colors.borders}`,
                borderRadius: theme.radius.sm,
                background: '#FFFFFF',
                color: theme.colors.text.primary,
                fontSize: '14px',
                fontWeight: 600,
                cursor: currentStep === 1 ? 'not-allowed' : 'pointer',
                opacity: currentStep === 1 ? 0.5 : 1
              }}
            >
              <ChevronLeft size={18} />
              Back
            </button>
            <button
              onClick={() => currentStep < 5 ? setCurrentStep(prev => prev + 1) : handleSubmit()}
              disabled={!canProceed() || loading}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 32px',
                border: 'none',
                borderRadius: theme.radius.sm,
                background: canProceed() && !loading ? theme.colors.primary : theme.colors.bg.tertiary,
                color: '#FFFFFF',
                fontSize: '14px',
                fontWeight: 600,
                cursor: canProceed() && !loading ? 'pointer' : 'not-allowed',
                opacity: loading ? 0.7 : 1
              }}
            >
              {loading ? 'Creating...' : currentStep === 5 ? 'Create Portfolio' : 'Continue'}
              {!loading && <ChevronRight size={18} />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BasicPortfolioWizard;
